import * as Sentry from "@sentry/nextjs";
import { useAtomValue } from "jotai";
import intersection from "lodash/intersection";
import { useRouter } from "next/router";
import qs from "qs";
import { useCallback, useEffect } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import {
  FollowAction,
  FollowType,
  getLoginDialogTitleAndDescription,
  isFollowType,
} from "scmp-sdk";
import type { DeepNonNullable } from "utility-types";

import { data } from "shared/data";
import { useCurrentPageType } from "shared/hooks";
import { accountAtom } from "shared/lib/account";
import { useRouterReplaceWithSanitizedKeys } from "shared/lib/router";
import { notEmpty, parseArray } from "shared/lib/utils";

import type {
  FollowButtonSource,
  FollowQueryStringPayload,
} from "scmp-app/components/follow-button/types";
import { computeGa4CustomizedParameters } from "scmp-app/components/follow-button/utilities";
import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { useUserService } from "scmp-app/lib/user-service/hooks";
import type { hooksFollowTargetEntityQuery } from "scmp-app/queries/__generated__/hooksFollowTargetEntityQuery.graphql";

import { SanitizedUrlKeys } from "./consts";

export const useFollowActionRedirect = () => {
  const { openLoginDialog } = useLoginDialogStateHelper();
  const { user } = useAtomValue(accountAtom);

  const { followAction } = useUserService();
  const { pathname, query } = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();

  const { fetchConsolidateFollowTargets } = useConsolidateFollowTargets();

  const currentPageType = useCurrentPageType();

  const execute = useCallback(
    async (
      currentLocation: string,
      followTargetEntityIds: DeepNonNullable<FollowQueryStringPayload>["action"]["follow"],
      source?: FollowButtonSource,
      utmSource?: string,
    ) => {
      const followTargets = Object.entries(followTargetEntityIds).reduce(
        (result, [type, entityIds]) => {
          if (!isFollowType(type)) return result;
          result[type] = parseArray(entityIds);
          return result;
        },
        {
          Author: [],
          Section: [],
          Topic: [],
        } as FollowTargetEntityIds,
      );
      const consolidatedFollowTargets = await fetchConsolidateFollowTargets(followTargets);

      Object.entries(consolidatedFollowTargets)
        .map(([type, consolidatedFollowTarget]) => {
          if (!isFollowType(type) || consolidatedFollowTarget.filteredEntityIds.length === 0)
            return null;
          return {
            consolidatedFollowTarget,
            type,
          };
        })
        .filter(notEmpty)
        .forEach(async ({ consolidatedFollowTarget, type }, index, validEntries) => {
          if (!user) {
            const { description, title } = getLoginDialogTitleAndDescriptionWithUtmSource({
              eventLabel: consolidatedFollowTarget.eventLabel,
              type,
              utmSource,
            });

            openLoginDialog({
              description,
              destination: currentLocation,
              ga4CustomParameter: {
                follow_name: consolidatedFollowTarget.eventLabel,
                follow_type: type.toLowerCase(),
                trigger_point: "follow",
              },
              title,
            });
            return;
          }

          await followAction(
            FollowAction.Follow,
            type,
            consolidatedFollowTarget.filteredEntityIds,
            () => {
              sendGA4Tracking({
                action: "sys",
                category: "follow",
                customized_parameters: {
                  ...computeGa4CustomizedParameters({
                    currentPageType,
                    entityIds: consolidatedFollowTarget.filteredEntityIds,
                    source,
                    type,
                  }),
                  action_type: "follow",
                },
                subcategory: "result",
              });
              // If this is the last entries, replace the url
              if (validEntries.length - 1 === index) {
                void replace(
                  SanitizedUrlKeys,
                  { pathname, query },
                  {
                    shallow: true,
                  },
                );
              }
            },
          );
        });
    },
    [
      currentPageType,
      fetchConsolidateFollowTargets,
      followAction,
      openLoginDialog,
      pathname,
      query,
      replace,
      user,
    ],
  );

  useEffect(() => {
    const parsedQueryString = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }) as unknown as FollowQueryStringPayload;
    if (!parsedQueryString.action?.follow) return;
    void execute(
      window.location.href,
      parsedQueryString.action.follow,
      parsedQueryString.source,
      parsedQueryString.utm_source,
    );
  }, [execute, pathname, query, replace]);
  return null;
};

const useConsolidateFollowTargets = () => {
  const environment = useRelayEnvironment();
  return {
    fetchConsolidateFollowTargets: useCallback(
      async (targetEntityIds: FollowTargetEntityIds) => {
        const computeFallbackConsolidatedFollowTarget = (followType: FollowType) => ({
          eventLabel: "",
          filteredEntityIds: targetEntityIds[followType],
        });
        const fallback = {
          [FollowType.Author]: computeFallbackConsolidatedFollowTarget(FollowType.Author),
          [FollowType.Section]: computeFallbackConsolidatedFollowTarget(FollowType.Section),
          [FollowType.Topic]: computeFallbackConsolidatedFollowTarget(FollowType.Topic),
        };

        try {
          const response = await fetchQuery<hooksFollowTargetEntityQuery>(
            environment,
            graphql`
              query hooksFollowTargetEntityQuery(
                $authorEntityIds: [String!]!
                $sectionEntityIds: [String!]!
                $topicEntityIds: [String!]!
                $topicApplicationId: String!
              ) {
                Author: authors(filter: { entityIds: $authorEntityIds }, first: 10) {
                  edges {
                    node {
                      entityId
                      name
                    }
                  }
                }
                Section: sections(filter: { entityIds: $sectionEntityIds }, first: 10) {
                  edges {
                    node {
                      entityId
                      name
                    }
                  }
                }
                Topic: topics(
                  filter: { entityIds: $topicEntityIds }
                  first: 10
                  applicationId: $topicApplicationId
                ) {
                  edges {
                    node {
                      entityId
                      name
                    }
                  }
                }
              }
            `,
            {
              authorEntityIds: targetEntityIds.Author,
              sectionEntityIds: targetEntityIds.Section,
              topicApplicationId: data.application.scmp.entityUuid,
              topicEntityIds: targetEntityIds.Topic,
            },
          ).toPromise();

          if (!response) return fallback;

          const computeConsolidatedFollowTarget = (followType: FollowType) => ({
            eventLabel: response[followType].edges.map(entity => entity.node.name).join(", ") ?? "",
            filteredEntityIds: intersection<string>(
              targetEntityIds[followType],
              response[followType].edges.map(entity => entity.node.entityId),
            ),
          });

          return {
            [FollowType.Author]: computeConsolidatedFollowTarget(FollowType.Author),
            [FollowType.Section]: computeConsolidatedFollowTarget(FollowType.Section),
            [FollowType.Topic]: computeConsolidatedFollowTarget(FollowType.Topic),
          } as ConsolidatedFollowTargets;
        } catch (error) {
          Sentry.captureException(error);
          return fallback;
        }
      },
      [environment],
    ),
  };
};

type ConsolidatedFollowTarget = {
  eventLabel: string;
  filteredEntityIds: string[];
};

type ConsolidatedFollowTargets = {
  [FollowType.Author]: ConsolidatedFollowTarget;
  [FollowType.Section]: ConsolidatedFollowTarget;
  [FollowType.Topic]: ConsolidatedFollowTarget;
};

type FollowTargetEntityIds = {
  [FollowType.Author]: string[];
  [FollowType.Section]: string[];
  [FollowType.Topic]: string[];
};

type GetLoginDialogTitleAndDescriptionParametersWithUtmSource = {
  eventLabel: string;
  type: FollowType;
  utmSource?: string;
};

const getLoginDialogTitleAndDescriptionWithUtmSource = (
  parameters: GetLoginDialogTitleAndDescriptionParametersWithUtmSource,
) => {
  const { eventLabel, type, utmSource } = parameters;

  const isFromAmp = utmSource === "google_amp";
  const isFromAppleNews = utmSource === "apple_news";

  if (type === FollowType.Topic && (isFromAmp || isFromAppleNews)) {
    return {
      description: `Log in or register for free in order to follow <span class="bold-text">${eventLabel}</span> and be notified when new content is published.`,
      title: "Keep updated",
    };
  }

  const { description, title } = getLoginDialogTitleAndDescription({
    campaignName: eventLabel,
    type,
  });

  return {
    description,
    title,
  };
};
